import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Box,
  Typography,
  Slider,
  IconButton,
  Paper,
  Divider,
  Grid
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { Question, Category } from '../FormBuilder';

interface QuestionDialogProps {
  open: boolean;
  question: Question | null;
  sectionId: number;
  categories: Category[];
  onClose: () => void;
  onSave: (question: Question) => void;
}

const QUESTION_TYPES = {
  yes_no: 'Sí / No / N/A',
  scale: 'Escala Numérica',
  text: 'Texto Libre',
  number: 'Número',
  select: 'Selección Única',
  multiple: 'Selección Múltiple',
  date: 'Fecha',
  time: 'Hora'
};

const QuestionDialog: React.FC<QuestionDialogProps> = ({
  open,
  question,
  sectionId,
  categories,
  onClose,
  onSave
}) => {
  const [formValues, setFormValues] = useState<Partial<Question>>({
    texto: '',
    descripcion: '',
    tipo: 'yes_no',
    obligatoria: true,
    ponderacion: 10,
    orden: 0,
    section_id: sectionId,
    category_id: undefined,
    permite_evidencia: true,
    permite_comentario: true,
    puntos_si: 100,
    puntos_no: 0,
    puntos_na: 50,
    opciones: [],
    config: {}
  });

  // Sort categories alphabetically (memoized to avoid infinite loop)
  const sortedCategories = React.useMemo(() => 
    [...categories].sort((a, b) => 
      a.nombre.localeCompare(b.nombre, 'es', { sensitivity: 'base' })
    ),
    [categories]
  );

  useEffect(() => {
    if (question) {
      // MODO EDICIÓN: Preservar TODOS los campos originales
      setFormValues({
        ...question,
        // NO sobrescribir section_id - ya viene correctamente del question
        section_id: question.section_id,
        orden: question.orden
      });
    } else {
      // MODO CREACIÓN: Valores por defecto
      setFormValues({
        texto: '',
        descripcion: '',
        tipo: 'yes_no',
        obligatoria: true,
        ponderacion: 10,
        orden: 0,
        section_id: sectionId, // ✅ Usar sectionId del tab activo
        category_id: sortedCategories[0]?.id, // Auto-select first category
        permite_evidencia: true,
        permite_comentario: true,
        puntos_si: 100,
        puntos_no: 0,
        puntos_na: 50,
        opciones: [],
        config: {}
      });
    }
  }, [question, open, sectionId, sortedCategories]);

  const handleSave = () => {
    if (!formValues.texto?.trim()) {
      return;
    }

    if (!formValues.category_id) {
      return;
    }

    onSave(formValues as Question);
  };

  const handleTypeChange = (tipo: string) => {
    const newValues: any = {
      ...formValues,
      tipo
    };

    // Valores por defecto según el tipo
    switch (tipo) {
      case 'yes_no':
        newValues.puntos_si = 100;
        newValues.puntos_no = 0;
        newValues.puntos_na = 50;
        break;
      case 'scale':
        newValues.config = { min: 1, max: 10, paso: 1 };
        break;
      case 'number':
        newValues.config = { min: 0, max: 100, paso: 1, unidad: '' };
        break;
      case 'select':
      case 'multiple':
        if (!newValues.opciones || newValues.opciones.length === 0) {
          newValues.opciones = [
            { value: 'opcion1', label: 'Opción 1', puntos: 100 },
            { value: 'opcion2', label: 'Opción 2', puntos: 50 }
          ];
        }
        break;
    }

    setFormValues(newValues);
  };

  const addOpcion = () => {
    const opciones = formValues.opciones || [];
    setFormValues({
      ...formValues,
      opciones: [
        ...opciones,
        {
          value: `opcion${opciones.length + 1}`,
          label: `Opción ${opciones.length + 1}`,
          puntos: 0
        }
      ]
    });
  };

  const updateOpcion = (index: number, field: string, value: any) => {
    const opciones = [...(formValues.opciones || [])];
    opciones[index] = { ...opciones[index], [field]: value };
    setFormValues({ ...formValues, opciones });
  };

  const deleteOpcion = (index: number) => {
    const opciones = formValues.opciones?.filter((_, i) => i !== index) || [];
    setFormValues({ ...formValues, opciones });
  };

  const renderTypeSpecificFields = () => {
    switch (formValues.tipo) {
      case 'yes_no':
        return (
          <Box>
            <Typography variant="caption" color="textSecondary" gutterBottom display="block">
              Configuración de Puntos
            </Typography>
            <Box display="flex" gap={2} mt={1}>
              <TextField
                label="Puntos SÍ"
                type="number"
                value={formValues.puntos_si || 0}
                onChange={(e) => setFormValues({ ...formValues, puntos_si: parseInt(e.target.value) })}
                fullWidth
                size="small"
              />
              <TextField
                label="Puntos NO"
                type="number"
                value={formValues.puntos_no || 0}
                onChange={(e) => setFormValues({ ...formValues, puntos_no: parseInt(e.target.value) })}
                fullWidth
                size="small"
              />
              <TextField
                label="Puntos N/A"
                type="number"
                value={formValues.puntos_na || 0}
                onChange={(e) => setFormValues({ ...formValues, puntos_na: parseInt(e.target.value) })}
                fullWidth
                size="small"
              />
            </Box>
          </Box>
        );

      case 'scale':
        return (
          <Box>
            <Typography variant="caption" color="textSecondary" gutterBottom display="block">
              Configuración de Escala (ej: 1 al 10)
            </Typography>
            <Box display="flex" gap={2} mt={1}>
              <TextField
                label="Mínimo"
                type="number"
                value={formValues.config?.min || 1}
                onChange={(e) => setFormValues({
                  ...formValues,
                  config: { ...formValues.config, min: parseInt(e.target.value) }
                })}
                fullWidth
                size="small"
              />
              <TextField
                label="Máximo"
                type="number"
                value={formValues.config?.max || 10}
                onChange={(e) => setFormValues({
                  ...formValues,
                  config: { ...formValues.config, max: parseInt(e.target.value) }
                })}
                fullWidth
                size="small"
              />
              <TextField
                label="Paso"
                type="number"
                value={formValues.config?.paso || 1}
                onChange={(e) => setFormValues({
                  ...formValues,
                  config: { ...formValues.config, paso: parseInt(e.target.value) }
                })}
                fullWidth
                size="small"
              />
            </Box>
          </Box>
        );

      case 'number':
        return (
          <Box>
            <Typography variant="caption" color="textSecondary" gutterBottom display="block">
              Configuración Numérica
            </Typography>
            <Box display="flex" gap={2} mt={1}>
              <TextField
                label="Mínimo"
                type="number"
                value={formValues.config?.min || 0}
                onChange={(e) => setFormValues({
                  ...formValues,
                  config: { ...formValues.config, min: parseInt(e.target.value) }
                })}
                fullWidth
                size="small"
              />
              <TextField
                label="Máximo"
                type="number"
                value={formValues.config?.max || 100}
                onChange={(e) => setFormValues({
                  ...formValues,
                  config: { ...formValues.config, max: parseInt(e.target.value) }
                })}
                fullWidth
                size="small"
              />
              <TextField
                label="Unidad (kg, cm, etc)"
                value={formValues.config?.unidad || ''}
                onChange={(e) => setFormValues({
                  ...formValues,
                  config: { ...formValues.config, unidad: e.target.value }
                })}
                fullWidth
                size="small"
              />
            </Box>
          </Box>
        );

      case 'select':
      case 'multiple':
        return (
          <Box display="flex" flexDirection="column" gap={2}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle2">
                Opciones de Respuesta
              </Typography>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={addOpcion}
              >
                Agregar Opción
              </Button>
            </Box>
            
            {(formValues.opciones || []).map((opcion, index) => (
              <Paper key={index} sx={{ p: 2 }}>
                <Box display="flex" gap={2} alignItems="flex-start">
                  <TextField
                    label="Etiqueta"
                    value={opcion.label}
                    onChange={(e) => updateOpcion(index, 'label', e.target.value)}
                    fullWidth
                  />
                  <TextField
                    label="Valor"
                    value={opcion.value}
                    onChange={(e) => updateOpcion(index, 'value', e.target.value)}
                    sx={{ maxWidth: 150 }}
                  />
                  <TextField
                    label="Puntos"
                    type="number"
                    value={opcion.puntos || 0}
                    onChange={(e) => updateOpcion(index, 'puntos', parseInt(e.target.value))}
                    sx={{ maxWidth: 100 }}
                  />
                  <IconButton
                    color="error"
                    onClick={() => deleteOpcion(index)}
                    disabled={(formValues.opciones?.length || 0) <= 1}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Paper>
            ))}
            
            {(formValues.opciones?.length || 0) === 0 && (
              <Typography variant="body2" color="textSecondary" textAlign="center">
                No hay opciones. Agregue al menos una opción.
              </Typography>
            )}
          </Box>
        );

      case 'text':
        return (
          <Typography variant="caption" color="textSecondary">
            El auditor podrá ingresar texto libre.
          </Typography>
        );

      case 'date':
      case 'time':
        return (
          <Typography variant="caption" color="textSecondary">
            El auditor seleccionará una {formValues.tipo === 'date' ? 'fecha' : 'hora'}.
          </Typography>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {question ? 'Editar Pregunta' : 'Nueva Pregunta'}
      </DialogTitle>
      
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} mt={1}>
          {/* Información básica */}
          <TextField
            label="Texto de la Pregunta"
            value={formValues.texto || ''}
            onChange={(e) => setFormValues({ ...formValues, texto: e.target.value })}
            required
            fullWidth
            autoFocus
            placeholder="Escriba aquí la pregunta que verá el auditor..."
          />

          <TextField
            label="Descripción / Ayuda (opcional)"
            value={formValues.descripcion || ''}
            onChange={(e) => setFormValues({ ...formValues, descripcion: e.target.value })}
            fullWidth
            placeholder="Instrucciones adicionales..."
            size="small"
          />

          {/* Tipo de pregunta + Categoría en la misma línea */}
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel id="tipo-pregunta-label" htmlFor="tipo-pregunta-input">Tipo de Pregunta</InputLabel>
                <Select
                  labelId="tipo-pregunta-label"
                  id="tipo-pregunta"
                  value={formValues.tipo || 'yes_no'}
                  label="Tipo de Pregunta"
                  onChange={(e) => handleTypeChange(e.target.value)}
                  inputProps={{
                    name: 'tipo-pregunta',
                    id: 'tipo-pregunta-input',
                  }}
                >
                  {Object.entries(QUESTION_TYPES).map(([value, label]) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              {/* Selector de categoría */}
              <FormControl fullWidth required size="small">
                <InputLabel id="category-label" htmlFor="category-input">Categoría</InputLabel>
                <Select
                  labelId="category-label"
                  id="category"
                  value={formValues.category_id || ''}
                  label="Categoría"
                  onChange={(e) => setFormValues({ ...formValues, category_id: e.target.value as number })}
                  inputProps={{
                    name: 'category',
                    id: 'category-input',
                  }}
                >
              {sortedCategories.map(category => (
                <MenuItem key={category.id} value={category.id}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box
                      component="span"
                      sx={{
                        width: 12,
                        height: 12,
                        bgcolor: category.color || '#2196F3',
                        borderRadius: '50%',
                        display: 'inline-block'
                      }}
                    />
                    {category.nombre}
                  </Box>
                </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Campos específicos del tipo */}
          {renderTypeSpecificFields()}

          <Divider />

          {/* Ponderación */}
          <Box>
            <Typography variant="caption" color="textSecondary" gutterBottom display="block">
              Ponderación: {formValues.ponderacion}% (suma de la categoría debe ser 100%)
            </Typography>
            <Slider
              value={formValues.ponderacion || 0}
              onChange={(_, value) => setFormValues({ ...formValues, ponderacion: value as number })}
              min={0}
              max={100}
              step={5}
              marks={[
                { value: 0, label: '0' },
                { value: 50, label: '50' },
                { value: 100, label: '100' }
              ]}
              valueLabelDisplay="auto"
              size="small"
            />
          </Box>

          {/* Opciones adicionales en línea horizontal */}
          <Box display="flex" gap={1} justifyContent="space-between" flexWrap="wrap">
            <FormControlLabel
              control={
                <Switch
                  checked={formValues.obligatoria || false}
                  onChange={(e) => setFormValues({ ...formValues, obligatoria: e.target.checked })}
                  size="small"
                />
              }
              label={<Typography variant="body2">Obligatoria</Typography>}
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={formValues.permite_evidencia || false}
                  onChange={(e) => setFormValues({ ...formValues, permite_evidencia: e.target.checked })}
                  size="small"
                />
              }
              label={<Typography variant="body2">Evidencia</Typography>}
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={formValues.permite_comentario || false}
                  onChange={(e) => setFormValues({ ...formValues, permite_comentario: e.target.checked })}
                  size="small"
                />
              }
              label={<Typography variant="body2">Comentarios</Typography>}
            />
          </Box>
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!formValues.texto?.trim() || !formValues.category_id}
        >
          {question ? 'Guardar' : 'Agregar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuestionDialog;
